import { useReducer, useEffect, useState } from 'react'
import * as R from 'ramda'
import { useSearch } from 'app/components/searchBar/useSearch'
import useDeepCompareEffect from 'use-deep-compare-effect'
const pageInfoActionType = {
  LOAD_START: 'LOAD_START',
  LOAD_END: 'LOAD_END',
  LOAD_SUCCESS: 'LOAD_SUCCESS',
  LOAD_FAILURE: 'LOAD_FAILURE',
  TOGGLE_SELECT: 'TOGGLE_SELECT',
  TOGGLE_SELECT_ALL: 'TOGGLE_SELECT_ALL',
  SET_FETCH_INIT_NO_DATA: 'SET_FETCH_INIT_NO_DATA',
  SET_PAGE_SIZE: 'SET_PAGE_SIZE',
  RESET_PAGE_NUMBER: 'RESET_PAGE_NUMBER',
}

const DEFAULT_PAGE_SIZE = 10
const DEFAULT_CURRENT_PAGE = 1

export const pageInfoActions = {
  loadStart: () => ({ type: pageInfoActionType.LOAD_START }),
  loadEnd: () => ({ type: pageInfoActionType.LOAD_END }),
  loadSuccess: (data, nextPageInfo, total, meta) => ({
    type: pageInfoActionType.LOAD_SUCCESS,
    data,
    page: R.dec(meta.currentPage),
    total: Math.ceil(total / meta.perPage),
    meta,
    totalData: total,
    nextPageInfo,
  }),
  loadFailure: (error) => ({
    type: pageInfoActionType.LOAD_FAILURE,
    error,
  }),
  toggleSelect: (id) => ({
    type: pageInfoActionType.TOGGLE_SELECT,
    id,
  }),
  toggleSelectAll: (checked) => ({
    type: pageInfoActionType.TOGGLE_SELECT_ALL,
    checked,
  }),
  setInitFetchNodata: (hasNoDataAtTheFirstTime) => ({
    type: pageInfoActionType.SET_FETCH_INIT_NO_DATA,
    hasNoDataAtTheFirstTime,
  }),
  setPageSize: (pageSize) => ({
    type: pageInfoActionType.SET_PAGE_SIZE,
    page: DEFAULT_CURRENT_PAGE,
    pageSize: pageSize || DEFAULT_PAGE_SIZE,
  }),
  resetPageNumber: () => ({
    type: pageInfoActionType.RESET_PAGE_NUMBER,
    page: DEFAULT_CURRENT_PAGE,
  }),
}

const initialState = {
  isOneTimeFetched: false,
  isLoading: false,
  page: DEFAULT_CURRENT_PAGE,
  pageSize: DEFAULT_PAGE_SIZE,
  total: 1,
  data: undefined,
  sorted: undefined,
  filtered: undefined,
  checkedIds: [],
  isCheckedAll: false,
  isIndeterminated: false,
  totalData: 0,
  isFirsttimeFetchingNoData: null,
}

const tableAPIPage = (pageInfo, searchText, filter, goToPageNo) => ({
  ...pageInfo,
  currentPage: R.max(
    !R.isNil(goToPageNo) ? R.inc(goToPageNo) : pageInfo.page,
    1,
  ),
  keyword: searchText,
  ...filter,
})

const pageInfoReducer = (state, action) => {
  switch (action.type) {
    case pageInfoActionType.LOAD_START: {
      return {
        ...state,
        isLoading: true,
      }
    }
    case pageInfoActionType.LOAD_END: {
      return {
        ...state,
        isLoading: false,
        isOneTimeFetched: true,
      }
    }
    case pageInfoActionType.LOAD_FAILURE: {
      return {
        ...state,
        error: action.error,
      }
    }
    case pageInfoActionType.LOAD_SUCCESS: {
      const { nextPageInfo, data, total, totalData, page } = action
      const checkedIds = R.filter(
        R.includes(R.__, R.map(R.prop('id'), data)),
        state.checkedIds,
      )

      const getIsFirsttimeFetchingNoData = () => {
        return state.isFirsttimeFetchingNoData === null
          ? totalData === 0
          : state.isFirsttimeFetchingNoData
      }

      return {
        ...state,
        ...nextPageInfo,
        page,
        data,
        total,
        totalData,
        checkedIds,
        isFirsttimeFetchingNoData: getIsFirsttimeFetchingNoData(),
        ...checkedProps(checkedIds, data),
      }
    }
    case pageInfoActionType.TOGGLE_SELECT: {
      const checkedIds = R.ifElse(
        R.includes(R.__, state.checkedIds),
        R.o(R.without(R.__, state.checkedIds), R.of),
        R.append(R.__, state.checkedIds),
      )(action.id)
      return {
        ...state,
        checkedIds,
        ...checkedProps(checkedIds, state.data),
      }
    }
    case pageInfoActionType.TOGGLE_SELECT_ALL: {
      const checkedIds = action.checked ? R.map(R.prop('id'), state.data) : []
      return {
        ...state,
        checkedIds,
        ...checkedProps(checkedIds, state.data),
      }
    }
    case pageInfoActionType.SET_FETCH_INIT_NO_DATA: {
      return {
        ...state,
        isFirsttimeFetchingNoData: action.hasNoDataAtTheFirstTime,
      }
    }
    case pageInfoActionType.SET_PAGE_SIZE: {
      return {
        ...state,
        pageSize: action.pageSize,
        page: action.page,
      }
    }
    case pageInfoActionType.RESET_PAGE_NUMBER: {
      return {
        ...state,
        page: action.page,
      }
    }
    default:
      return state
  }
}

const checkedProps = (checkedIds, data) => {
  const isCheckedAll = isCheckAll(checkedIds, data)
  return {
    checkedIds,
    isCheckedAll,
    isIndeterminated: isIndeterminated(checkedIds, isCheckedAll),
    checkedData: checkedData(checkedIds, data),
  }
}

const isCheckAll = (checkedIds, data) =>
  R.ifElse(
    R.isEmpty,
    R.always(false),
    R.eqBy(R.length, checkedIds),
  )(R.defaultTo([], data))

const checkedData = (checkedIds, data) =>
  R.filter(R.propSatisfies(R.includes(R.__, checkedIds), 'id'), data)

const isIndeterminated = (checkedIds, isCheckedAll) =>
  !R.isEmpty(checkedIds) && !isCheckedAll

export const useTableFetch = (fetchData, initialFilter = {}) => {
  const { searchText, debounceSearchText, onChange, onReset } = useSearch()
  const [filter, setFilter] = useState(initialFilter)
  const [pageInfo, dispatchPageInfo] = useReducer(pageInfoReducer, initialState)

  useEffect(() => {
    startFetchData()
    fetchData(tableAPIPage(pageInfo, searchText, filter))
      .then(({ data, meta }) => {
        dispatchPageInfo(
          pageInfoActions.loadSuccess(data, pageInfo, meta.total, meta),
        )
      })
      .catch(setError)
      .finally(() => {
        dispatchPageInfo(pageInfoActions.loadEnd())
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onChangeFilterByKey = (key) => (e) => {
    const value = R.pathOr(e, ['target', 'value'], e)
    dispatchPageInfo(pageInfoActions.resetPageNumber())
    setFilter({ ...filter, [key]: value })
  }

  const setError = (error) => {
    dispatchPageInfo(pageInfoActions.loadFailure(error))
  }

  const reloadData = (pageInfo, searchText, filter, pageIndex) => {
    fetchData(tableAPIPage(pageInfo, searchText, filter, pageIndex))
      .then(({ data, meta }) => {
        dispatchPageInfo(
          pageInfoActions.loadSuccess(data, pageInfo, meta.total, meta),
        )
      })
      .catch(setError)
      .finally(() => {
        dispatchPageInfo(pageInfoActions.loadEnd())
      })
  }

  const onFetchData = (state) => {
    const nextPage = R.over(
      R.lensProp('page'),
      R.when(R.anyPass([isNaN, R.equals(-1)]), R.always(1)),
      R.pick(['page', 'pageSize', 'sorted', 'filtered'], state),
    )
    dispatchPageInfo(pageInfoActions.loadStart())
    reloadData(nextPage, searchText, filter)
  }

  //MARK : isForceResetSetInitFetchNodata If it is false.
  //MARK : It will remain firsttime init fetching nodata the same

  const refetchData = (isForceResetSetInitFetchNodata = true) => {
    if (isForceResetSetInitFetchNodata) {
      dispatchPageInfo(pageInfoActions.setInitFetchNodata(null))
    }
    dispatchPageInfo(pageInfoActions.loadStart())
    return reloadData(pageInfo, searchText, filter)
  }

  const startFetchData = () => {
    dispatchPageInfo(pageInfoActions.loadStart())
  }

  const toggleSelect = R.compose(dispatchPageInfo, pageInfoActions.toggleSelect)

  const toggleSelectAll = R.compose(
    dispatchPageInfo,
    pageInfoActions.toggleSelectAll,
  )

  const onPageChange = (pageIndex) => {
    dispatchPageInfo(pageInfoActions.loadStart())
    fetchData(tableAPIPage(pageInfo, searchText, filter, pageIndex))
      .then(({ data, meta }) => {
        dispatchPageInfo(
          pageInfoActions.loadSuccess(data, pageInfo, meta.total, meta),
        )
      })
      .catch(setError)
      .finally(() => {
        dispatchPageInfo(pageInfoActions.loadEnd())
      })
  }

  // to do arg. column, shiftKey
  const onSortedChange = (newSorted) => {
    setFilter({
      ...filter,
      sorted: newSorted,
    })
    startFetchData()
    dispatchPageInfo(pageInfoActions.resetPageNumber())
  }
  // to do arg pageIndex
  const onPageSizeChange = (pageSize) => {
    dispatchPageInfo(pageInfoActions.setPageSize(pageSize))
  }

  const endFetchData = () => {
    dispatchPageInfo(pageInfoActions.loadEnd())
  }

  const onClearFilter = (data) => {
    setFilter(data)
  }

  useDeepCompareEffect(() => {
    if (!R.isNil(pageInfo.data)) {
      startFetchData()
      reloadData(pageInfo, debounceSearchText, filter)
    }
  }, [filter])

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!R.isNil(pageInfo.data)) {
      startFetchData()
      reloadData(
        R.set(R.lensProp('page'), 0, pageInfo),
        debounceSearchText,
        filter,
      )
    }
  }, [debounceSearchText])
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    dispatchPageInfo(pageInfoActions.resetPageNumber())
  }, [searchText])

  useEffect(() => {
    if (R.prop('pageSize', pageInfo) > DEFAULT_PAGE_SIZE) {
      startFetchData()
      reloadData(pageInfo, searchText, filter)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [R.prop('pageSize', pageInfo)])

  return {
    pageInfo,
    onFetchData,
    onSortedChange,
    onPageSizeChange,
    refetchData,
    startFetchData,
    endFetchData,
    showErrorDialogue: setError,
    setError,
    toggleSelect,
    toggleSelectAll,
    onChangeFilterByKey,
    onPageChange,
    onClearFilter,
    filter,
    hasData: R.compose(
      R.not,
      R.anyPass([R.isNil, R.equals(0)]),
      R.prop('total'),
    )(pageInfo),
    showPagination: showPagination(pageInfo),
    defaultPageSize: DEFAULT_PAGE_SIZE,
    search: {
      text: searchText,
      onChange,
      onReset,
    },
  }
}

export const showPagination = (pageInfo) => {
  if (pageInfo.data) {
    return R.path(['totalData'], pageInfo) > DEFAULT_PAGE_SIZE
  }
  return true
}

export const pageToSortPairs = R.compose(
  R.when(
    R.allPass([R.o(R.not, R.isNil), R.has('desc'), R.has('id')]),
    ({ id, desc }) => `${id}:${desc === true ? 'DESC' : 'ASC'}`,
  ),
  (d) => d && d.length && R.head(d),
  R.prop('sorted'),
)
